import * as vp from 'vopay';
import { FunctionComponent, useState } from 'react';
import styles from './ElinxHistory.module.scss';
import { useTranslation } from 'react-i18next';
import { Button } from 'UI';

const { Box, Select, TextField, DateRangePicker, AVAILABLE_TIMEZONES } = vp;

type ElinxSearchParams = {
  requestStatus: string;
  searchTerm: string;
  timeRange: [vp.Timestamp, vp.Timestamp];
  timeZone: string;
};

type SearchFormProps = {
  requestStatus: string;
  timeZone: vp.Timezone;
  timeRange?: [vp.Timestamp, vp.Timestamp];
  searchTerm: string;
  initialTimeRange: [vp.Timestamp, vp.Timestamp];
  hasInvalidEndDate?: boolean;
  shouldShowEndDateError?: boolean;
  setShouldShowEndDateError?: (event: boolean) => void;
  search: (searchParams: ElinxSearchParams) => void;
};

type FC = FunctionComponent<SearchFormProps>;

const ElinxHistoryForm: FC = ({
  requestStatus,
  timeZone,
  searchTerm,
  initialTimeRange,
  hasInvalidEndDate,
  shouldShowEndDateError,
  setShouldShowEndDateError,
  search,
}: SearchFormProps) => {
  const [currentRequestStatus, setCurrentRequestStatus] = useState<string>(requestStatus);
  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>(searchTerm);
  const [timeRange, setTimeRange] = useState<[vp.Timestamp, vp.Timestamp]>(initialTimeRange);
  const [currentTimeZone, setCurrentTimeZone] = useState<string>(timeZone);
  const { t } = useTranslation('translations', { keyPrefix: 'ElinxHistory.SearchForm' });

  const requestStatusOptions = [
    { value: 'all', label: t('Options.RequestStatusOptions.all') },
    { value: 'pending', label: t('Options.RequestStatusOptions.pending') },
    { value: 'cancelled', label: t('Options.RequestStatusOptions.cancelled') },
    { value: 'completed', label: t('Options.RequestStatusOptions.completed') },
  ];

  const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const initialTimeZones = AVAILABLE_TIMEZONES.map((tz) => ({
    label: tz,
    value: tz,
  })).filter((tz) => !tz.value.toLowerCase().includes('utc') && !tz.value.includes(timezoneName));
  const timeZoneOptions = [
    {
      label: 'UTC',
      value: 'UTC',
    },
    {
      label: timezoneName,
      value: timezoneName,
    },
    ...initialTimeZones,
  ];

  return (
    <Box flx="row" gap="sm" wrap="wrap">
      <div className={styles.formContainer}>
        <DateRangePicker
          onChange={(range) => {
            setTimeRange(range);
          }}
          initialTime={initialTimeRange}
          triggerVariant="outlineShadow"
          hasInvalidEndDate={hasInvalidEndDate}
          shouldShowEndDateError={shouldShowEndDateError}
          setShouldShowEndDateError={setShouldShowEndDateError}
        />
        <Select
          version="2"
          placeholder={t('status')}
          options={requestStatusOptions}
          name="status"
          value={currentRequestStatus}
          onBlur={() => {}}
          onChange={(e) => {
            setCurrentRequestStatus(e.target.value);
          }}
        />
        <Select
          version="2"
          placeholder={t('timezone')}
          options={timeZoneOptions}
          name="timeZone"
          value={currentTimeZone}
          onBlur={() => {}}
          onChange={(e) => {
            setCurrentTimeZone(e.target.value);
          }}
        />
        <TextField
          key="AccountSearchKeyword"
          name="AccountSearchKeyword"
          placeholder={t('keyword')}
          value={currentSearchTerm}
          onBlur={() => {}}
          onChange={(e) => {
            setCurrentSearchTerm(e.target.value);
          }}
          variant="outlineShadow"
        />
        <Button
          id="search-elinx"
          size="small"
          colorScheme="primary"
          onClick={() => {
            search({
              requestStatus: currentRequestStatus,
              searchTerm: currentSearchTerm,
              timeRange,
              timeZone: currentTimeZone,
            });
          }}
        >
          {t('search')}
        </Button>
      </div>
    </Box>
  );
};

export default ElinxHistoryForm;
